/**
 * AI:
 * Status: "confirmed"
 * Type: Component
 * SubType: CrudIntegration
 * Reason: Top-down manual adjustment of an existing cp.ProvisionContract and/or one of its
 *         cp.ProvisionContractAgent rows. Hosts two mvs-crud-object forms in capture-only mode and
 *         owns the write sequence itself, so both rows are written in one controlled, resumable
 *         order and nothing is written before the user confirms.
 */
import {Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges} from '@angular/core';
import {Observable, of, Subscription} from 'rxjs';
import {switchMap} from 'rxjs/operators';

import {DtoDetail} from 'features/core/shared/dto/dto.detail';
import {DtoImportObjectContext} from 'features/core/shared/dto/dto.import.object.context';
import {MvsFormControlOverwrite} from 'features/core/shared/logic/form/logic/mvs-form-control-overwrite';
import {MvsFormFieldAccessEnum} from 'features/core/shared/logic/form/logic/mvs-form-field-access.enum';
import {MvsMessageService} from 'features/core/shared/service/message/mvs-message.service';
import {MvsObjectService} from 'features/core/shared/service/object/mvs-object.service';
import {
    ObjectChangeInformation,
    ObjectChangeInformationActionEnum
} from 'features/core/shared/logic/object-change-information';

import {ProvisionContractDto} from '../../../model/dto/entity/provision-contract.dto';
import {ProvisionContractAgentDto} from '../../../model/dto/entity/provision-contract-agent.dto';
import {CpFeatureRight} from '../../../service/domain/cp-feature-right';
import {
    CpWizardFieldState,
    CpProvisionContractWizardStateService
} from '../../../service/domain/cp-provision-contract-wizard-state.service';

/** The steps of the adjust wizard; which of them are ACTIVE follows from the chosen scope. */
type CpProvisionContractAdjustStep = 'scope' | 'contract' | 'agent' | 'summary';

/** What the run adjusts. */
export type CpProvisionContractAdjustScope = 'contract' | 'agent' | 'both';

/**
 * Anpassungs-Wizard: manuelle Anpassung einer bestehenden Provisionszeile
 * (`cp.ProvisionContract`) und/oder eines bestehenden Provisionssatzes
 * (`cp.ProvisionContractAgent`), top-down in EINEM Durchlauf.
 *
 * <h2>Mechanics — identical to the create wizard, and for the same reasons</h2>
 * Both embedded {@code <mvs-crud-object>} instances run with {@code hideDefaultActions = true}, i.e.
 * CAPTURE ONLY. The write order, the single-flight lock and the resume points belong to this
 * component and to {@link CpProvisionContractWizardStateService}; the forms never write. See the
 * TSDoc of {@code CpProvisionContractCreateWizardComponent} for why the form-owned-save shape of the
 * older address/bank wizards is not used.
 *
 * <h2>Why an UPDATE still needs resume points</h2>
 * An update is repeatable in principle, so it is tempting to let a retry simply run the whole
 * sequence again. It must not: by the time the second write of the sequence has failed, the first
 * one has already landed, and re-sending the captured SNAPSHOT would overwrite a row that has moved
 * on. {@code ensureContractUpdated} / {@code ensureAgentUpdated} therefore keep a flag each, so
 * "Erneut versuchen" performs exactly the writes that still have to happen.
 *
 * <h2>What this wizard deliberately does NOT offer</h2>
 * <ul>
 *   <li><b>The Berater of a Provisionssatz.</b> {@code ProvisionContractAgentForm} renders the field
 *       read-only and that protection is exactly right here: changing the agent of an existing row
 *       is a book-keeping operation — the bookings of the previous agent have to be reversed and
 *       re-booked, and the row has to be stamped {@code agentManuallyChanged} so a later run does
 *       not pull the agent back. That is what {@code POST /{id}/changeAgent} does, and it is what
 *       the existing "Berater wechseln" dialog calls. This wizard points at it instead of writing
 *       the foreign key behind its back.</li>
 *   <li><b>The Ereignisdatum ({@code applicationDate}).</b> Write-once by design; the form renders it
 *       read-only and {@code ProvisionContractOnChange.assertApplicationDateUnchanged} refuses the
 *       change on the write path. The wizard leaves both in place.</li>
 *   <li><b>The engine-owned fields.</b> Excluded from the form entirely — the next ProvisionRun owns
 *       them. What will happen to each of them is stated by name in the closing step rather than
 *       left as an empty value.</li>
 * </ul>
 *
 * <h2>The closing step splits its fields into two honest groups</h2>
 * DEFERRED are the four nullable fields of the Provisionszeile that the next run fills in; they carry
 * the pending sentence. CAPTURED are {@code eligibility} and the five liability fields, whose columns
 * are NOT NULL without a default — the row cannot exist without them, so they are stated rather than
 * computed and the panel shows the value that stands. The pending sentence must never appear over a
 * captured field: it would describe a value the user set as provisional.
 *
 * <p>An amount overwrite entered here still needs its mandatory reason — that rule lives in
 * {@code ProvisionContractAgentOnChange.assertOverwriteReasonPresent} and its German refusal is
 * shown as the server sends it. This wizard re-implements no check.
 *
 * <p>This wizard is ADDITIVE. The three existing action dialogs of the module are untouched and keep
 * running next to it.
 *
 * German UI text with real umlauts, English code and comments (module convention).
 */
@Component({
    selector: 'cp-provision-contract-adjust-wizard',
    templateUrl: './cp-provision-contract-adjust-wizard.component.html',
    styleUrls: ['./cp-provision-contract-adjust-wizard.component.scss'],
    providers: [CpProvisionContractWizardStateService],
    standalone: false
})
export class CpProvisionContractAdjustWizardComponent implements OnInit, OnChanges, OnDestroy {

    /**
     * Fields of the Provisionszeile the ENGINE owns or that are write-once.
     *
     * {@link MvsFormFieldAccessEnum#EXCLUDED} — no control at all, so the key never reaches the
     * payload and the generic merge leaves the column untouched. HIDDEN would be actively wrong on
     * an UPDATE: it submits the form's snapshot, and a snapshot taken before the last run would
     * silently roll a computed value back.
     *
     * <p>{@code eligibility} is deliberately NOT in this list. It is a field the USER states — its
     * column is NOT NULL without a default, so the value on the row was typed by someone, and a
     * "manuelle Anpassung" that cannot correct it would be the one screen where the correction is
     * needed and missing. Leaving it out would also make the closing step lie: the panel would show
     * "noch nicht erfasst" over a row that carries a value.
     */
    protected static readonly CONTRACT_ENGINE_FIELDS: string[] = [
        'contractDtoId',
        'provisionTypeDtoId',
        'provisionTypeContractEventDtoId',
        'applicationDate',
        'endDate',
        'provisionBaseDtoId',
        'appliedBaseCalculationMode',
        'baseReferenceAmountMonthly',
        'baseAmountMonthly',
        'baseAmountTotal',
        'agentsResolved',
        'triggeringContractPriceId'
    ];

    /**
     * Fields of the Provisionssatz the ENGINE owns, or that have their own action endpoint.
     *
     * <p>The five liability fields are deliberately NOT in this list, for the same reason as
     * {@code eligibility} above: they are NOT NULL without a default, i.e. stated rather than
     * computed, and correcting a liability frame by hand is exactly what this wizard is for.
     * {@code outOfLiabilityAmount} and {@code paymentFreeMonths} stay excluded — those two really are
     * roll-forward results the run maintains.
     */
    protected static readonly AGENT_ENGINE_FIELDS: string[] = [
        'provisionContractDtoId',
        'contractDtoId',
        'provisionTypeDtoId',
        'provisionTypeContractEventDtoId',
        'provisionAgentDtoId',
        'source',
        'agentManuallyChanged',
        'outOfLiabilityAmount',
        'paymentFreeMonths',
        'cancellationLiabilityAmount',

        // The ten fields the run owns outright — see DEFERRED_CONTRACT_AGENT_FIELDS. Excluded here
        // for the same reason as on the create path: offering a field whose only writer is the run
        // invites an edit the next run replaces without a word.
        'liabilityTypeAssignmentDtoId',
        'liabilityTypeDtoId',
        'appliedLiabilitySource',
        'provisionTypeMatrixDtoId',
        'appliedCalcMode',
        'appliedFactor',
        'appliedReservePercentage',
        'provisionAmount',
        'reserveTargetAmount',
        'provisionTargetAmount'
    ];

    initialized: boolean = false;

    /** Visibility of the dialog; intended as a two-way binding ({@code [(visible)]}). */
    @Input() visible: boolean = false;
    @Output() visibleChange = new EventEmitter<boolean>();

    /** um right {@code cp_agentChange} — optimistic default, demoted on a server 403. */
    @Input() canAgentChange: boolean = true;

    /** The Provisionszeile to adjust; null = the run can only adjust the Provisionssatz. */
    @Input() provisionContractId: number;

    /** The Provisionssatz to adjust; null = the run can only adjust the Provisionszeile. */
    @Input() provisionContractAgentId: number;

    /** Optional ambient context handed to both embedded forms; scopes the backend value lists. */
    @Input() importObjectContext: DtoImportObjectContext;

    /** Fires after the whole run succeeded. */
    @Output() onAdjusted = new EventEmitter<void>();

    /** Fires when the server refused the right with a 403 — the demotion signal. */
    @Output() onForbidden = new EventEmitter<void>();

    /** The user abandoned the run. */
    @Output() onCancel = new EventEmitter<void>();

    /** What this run adjusts; preselected when only one of the two rows was handed in. */
    scope: CpProvisionContractAdjustScope = null;

    /** Field access of the Provisionszeile form; a stable reference, built once per run. */
    contractFormControlOverwrite: MvsFormControlOverwrite;

    /** Field access of the Provisionssatz form; a stable reference, built once per run. */
    agentFormControlOverwrite: MvsFormControlOverwrite;

    /** Context of both embedded forms. */
    formImportObjectContext: DtoImportObjectContext;


    /** The block-head explanation, from the ONE place both wizards and the detail view share. */
    readonly deferredExplanation: string = CpProvisionContractWizardStateService.DEFERRED_EXPLANATION;

    /** True once the whole run succeeded — the dialog then shows its result panel. */
    completed: boolean = false;

    protected subscription: Subscription = new Subscription();

    constructor(
        public state: CpProvisionContractWizardStateService,
        protected messageService: MvsMessageService,
        protected mvsObjectService: MvsObjectService) {
    }

    ngOnInit(): void {

        this.initialized = true;

        if (this.visible) {
            this.refreshComponent();
        }
    }

    ngOnChanges(changes: SimpleChanges): void {

        if (!this.initialized) {
            return;
        }

        if ((changes['visible'] && this.visible)
            || changes['provisionContractId']
            || changes['provisionContractAgentId']) {

            if (this.visible) {
                this.refreshComponent();
            }
        }
    }

    ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    /** Return the whole run to its starting state; rebuilt fresh on every open. */
    refreshComponent(): void {

        this.state.reset();

        this.completed = false;

        this.formImportObjectContext = this.importObjectContext
            ? this.importObjectContext.copy()
            : DtoImportObjectContext.createEmpty();

        this.state.importObjectContext = this.formImportObjectContext;

        this.contractFormControlOverwrite = this.buildContractFormControlOverwrite();
        this.agentFormControlOverwrite = this.buildAgentFormControlOverwrite();

        this.scope = this.resolveInitialScope();
        this.rebuildSteps();

        if (this.provisionContractAgentId) {
            // Purely for the closing step: the ten run-owned fields are excluded from the form, so
            // the draft never carries them and only the stored row can say what stands there.
            this.subscription.add(
                this.state.loadExistingAgent(this.provisionContractAgentId).subscribe({
                    error: () => {
                        // Not fatal: the write path does not depend on this read. Without it the ten
                        // simply fall back to the pending sentence, which is the honest answer when
                        // the wizard could not find out what stands there.
                    }
                })
            );
        }
    }

    // ---------------------------------------------------------------------------------------- //
    // Scope
    // ---------------------------------------------------------------------------------------- //

    hasContract(): boolean {
        return !!this.provisionContractId;
    }

    hasAgent(): boolean {
        return !!this.provisionContractAgentId;
    }

    /** Neither row was handed in — the run cannot start. The template says so. */
    isTargetMissing(): boolean {
        return !this.hasContract() && !this.hasAgent();
    }

    /**
     * The scope the run starts with.
     *
     * Preselected when only one of the two rows exists: there is nothing to choose then, and the
     * scope step renders as a decided step marked "Vorgegeben" rather than asking a question with a
     * single answer.
     */
    protected resolveInitialScope(): CpProvisionContractAdjustScope {

        if (this.hasContract() && !this.hasAgent()) {
            return 'contract';
        }

        if (!this.hasContract() && this.hasAgent()) {
            return 'agent';
        }

        return null;
    }

    /** Whether the scope was decided for the user rather than chosen by them. */
    isScopeFixed(): boolean {
        return this.hasContract() !== this.hasAgent();
    }

    handleSelectScope(scope: CpProvisionContractAdjustScope): void {

        if (this.state.isLocked() || this.state.saveBusy) {
            return;
        }

        this.scope = scope;
        this.rebuildSteps();
    }

    isScopeSelected(scope: CpProvisionContractAdjustScope): boolean {
        return this.scope === scope;
    }

    getScopeLabel(): string {

        if (this.scope === 'contract') {
            return 'Nur Provisionszeile';
        }

        if (this.scope === 'agent') {
            return 'Nur Provisionssatz';
        }

        if (this.scope === 'both') {
            return 'Provisionszeile und Provisionssatz';
        }

        return 'Noch nicht gewählt';
    }

    adjustsContract(): boolean {
        return this.scope === 'contract' || this.scope === 'both';
    }

    adjustsAgent(): boolean {
        return this.scope === 'agent' || this.scope === 'both';
    }

    /**
     * Rebuild the active steps for the chosen scope.
     *
     * The scope step itself stays in the list even when it was decided for the user: it is the
     * decision the whole run rests on, so it has to remain visible as a summary card.
     */
    protected rebuildSteps(): void {

        const steps: CpProvisionContractAdjustStep[] = ['scope'];

        if (this.adjustsContract()) {
            steps.push('contract');
        }

        if (this.adjustsAgent()) {
            steps.push('agent');
        }

        steps.push('summary');

        this.state.steps = steps;

        if (this.state.activeStepIndex >= steps.length) {
            this.state.activeStepIndex = steps.length - 1;
        }
    }

    // ---------------------------------------------------------------------------------------- //
    // Form configuration
    // ---------------------------------------------------------------------------------------- //

    /** Field access of the Provisionszeile form — see {@link #CONTRACT_ENGINE_FIELDS}. */
    protected buildContractFormControlOverwrite(): MvsFormControlOverwrite {

        const overwrite = new MvsFormControlOverwrite();

        for (const field of CpProvisionContractAdjustWizardComponent.CONTRACT_ENGINE_FIELDS) {
            overwrite.addField(field, MvsFormFieldAccessEnum.EXCLUDED);
        }

        return overwrite;
    }

    /** Field access of the Provisionssatz form — see {@link #AGENT_ENGINE_FIELDS}. */
    protected buildAgentFormControlOverwrite(): MvsFormControlOverwrite {

        const overwrite = new MvsFormControlOverwrite();

        for (const field of CpProvisionContractAdjustWizardComponent.AGENT_ENGINE_FIELDS) {
            overwrite.addField(field, MvsFormFieldAccessEnum.EXCLUDED);
        }

        return overwrite;
    }

    // ---------------------------------------------------------------------------------------- //
    // Capture
    // ---------------------------------------------------------------------------------------- //

    /**
     * Would the run pass this Provisionszeile by, given what is captured?
     *
     * Delegates to the state container, which holds the rule and its evidence. Rendered as a warning
     * next to the field and again in the closing step — never as a block, because the choice is the
     * user's to make.
     */
    isOutsideRunSelection(): boolean {
        return this.state.isOutsideRunSelection(this.state.contractDraft);
    }

    /** The German warning, in ONE place so the two render sites cannot drift apart. */
    getSelectionWarning(): string {

        return 'Mit einer anderen Relevanz als „Provisionsrelevant" wird diese Provisionszeile vom '
            + 'Berechnungslauf möglicherweise nicht aufgegriffen — dann bleiben die oben genannten '
            + 'Felder dauerhaft leer. Setzen Sie alternativ eine Relevanz-Überschreibung, die den Lauf '
            + 'ebenfalls auf die Zeile führt.';
    }

    handleContractFormChange(value: DtoDetail): void {
        this.state.contractDraft = value;
    }

    handleContractSubmitStateChange(submittable: boolean): void {
        this.state.contractSubmittable = submittable;
    }

    handleAgentFormChange(value: DtoDetail): void {
        this.state.agentDraft = value;
    }

    handleAgentSubmitStateChange(submittable: boolean): void {
        this.state.agentSubmittable = submittable;
    }

    // ---------------------------------------------------------------------------------------- //
    // Navigation
    // ---------------------------------------------------------------------------------------- //

    isStepActive(step: CpProvisionContractAdjustStep): boolean {
        return this.state.isStepActive(step);
    }

    isStepDecided(step: CpProvisionContractAdjustStep): boolean {
        return this.state.isStepDecided(step);
    }

    hasStep(step: CpProvisionContractAdjustStep): boolean {
        return this.state.steps.indexOf(step) >= 0;
    }

    getStepNumber(step: CpProvisionContractAdjustStep): number {
        return this.state.getStepNumber(step);
    }

    canGoBack(): boolean {
        return this.state.activeStepIndex > 0 && !this.state.saveBusy && !this.state.isLocked();
    }

    canGoForward(): boolean {

        if (this.state.saveBusy || this.state.isLastStep()) {
            return false;
        }

        if (this.isStepActive('scope')) {
            return !!this.scope;
        }

        if (this.isStepActive('contract')) {
            return this.state.contractSubmittable && !!this.state.contractDraft;
        }

        if (this.isStepActive('agent')) {
            return this.state.agentSubmittable && !!this.state.agentDraft;
        }

        return false;
    }

    handleBack(): void {

        if (!this.canGoBack()) {
            return;
        }

        this.state.goToStep(this.state.activeStepIndex - 1);
    }

    handleForward(): void {

        if (!this.canGoForward()) {
            return;
        }

        this.state.goToStep(this.state.activeStepIndex + 1);
    }

    canChangeStep(step: CpProvisionContractAdjustStep): boolean {

        if (step === 'scope' && this.isScopeFixed()) {
            return false;
        }

        return this.isStepDecided(step) && !this.state.isLocked() && !this.state.saveBusy;
    }

    handleChangeStep(step: CpProvisionContractAdjustStep): void {

        if (!this.canChangeStep(step)) {
            return;
        }

        this.state.goToStep(this.state.steps.indexOf(step));
    }

    // ---------------------------------------------------------------------------------------- //
    // Closing step — the honesty duty, in its two honest halves
    // ---------------------------------------------------------------------------------------- //

    /**
     * The four deferred fields of the Provisionszeile — never asked, filled by the next run, and
     * shown with the pending sentence. Never an empty cell.
     */
    getDeferredContractFields(): CpWizardFieldState[] {
        return this.state.describeDeferredContractFields(this.state.contractDraft);
    }

    /** The Provisionszeile field the user states — shown with the value that stands, never the hint. */
    getCapturedContractFields(): CpWizardFieldState[] {
        return this.state.describeCapturedContractFields(this.state.contractDraft);
    }

    /**
     * The ten deferred fields of the Provisionssatz.
     *
     * Described against the LOADED row, never the draft: the ten are excluded from the form, so the
     * draft cannot carry them. A field that already holds a run result shows it, labelled
     * "aus der bestehenden Zeile"; only the ones still empty keep the pending sentence. Reading the
     * draft here would report all ten as still owed on a row the run may have completed long ago.
     */
    getDeferredAgentFields(): CpWizardFieldState[] {

        return this.state.describeDeferredAgentFields(
            this.state.existingAgent, CpProvisionContractWizardStateService.EXISTING_HINT);
    }

    /** The five liability fields of the Provisionssatz the user states, same rule. */
    getCapturedAgentFields(): CpWizardFieldState[] {
        return this.state.describeCapturedAgentFields(this.state.agentDraft);
    }

    // ---------------------------------------------------------------------------------------- //
    // Save
    // ---------------------------------------------------------------------------------------- //

    canSave(): boolean {

        if (!this.canAgentChange || !this.state.isLastStep() || this.state.saveBusy) {
            return false;
        }

        if (this.adjustsContract() && !this.state.contractDraft) {
            return false;
        }

        if (this.adjustsAgent() && !this.state.agentDraft) {
            return false;
        }

        return this.adjustsContract() || this.adjustsAgent();
    }

    getSubmitTooltip(): string {

        if (!this.canAgentChange) {
            return CpFeatureRight.deniedMessage(CpFeatureRight.AGENT_CHANGE);
        }

        if (this.adjustsContract() && !this.state.contractDraft) {
            return 'Bitte die Provisionszeile prüfen und ergänzen.';
        }

        if (this.adjustsAgent() && !this.state.agentDraft) {
            return 'Bitte den Provisionssatz prüfen und ergänzen.';
        }

        return 'Änderungen übernehmen.';
    }

    handleSave(): void {

        if (!this.canSave()) {
            return;
        }

        this.runSaveSequence();
    }

    /** Retry after a failure — the SAME path; every write checks its own resume point first. */
    handleRetrySave(): void {

        if (this.state.saveBusy) {
            return;
        }

        this.runSaveSequence();
    }

    /**
     * The write sequence: the Provisionszeile first, then the Provisionssatz.
     *
     * The order is not cosmetic. The child carries denormalised copies of the parent's contract,
     * provision type and event, and {@code ProvisionContractAgentOnChange} refuses a row whose
     * copies disagree with the parent — so a change on the parent has to have landed before the
     * child is judged against it.
     */
    protected runSaveSequence(): void {

        if (!this.state.beginSaveSequence()) {
            return;
        }

        this.subscription.add(
            this.writeContract().pipe(
                switchMap(() => this.writeAgent())
            ).subscribe({
                next: () => this.handleSaveSuccess(),
                error: (error: any) => this.handleSaveError(error)
            })
        );
    }

    /** The parent write, or a pass-through when the run does not adjust the Provisionszeile. */
    protected writeContract(): Observable<ProvisionContractDto> {

        if (!this.adjustsContract()) {
            return of(null);
        }

        return this.state.ensureContractUpdated(this.buildContractPayload());
    }

    /** The child write, or a pass-through when the run does not adjust the Provisionssatz. */
    protected writeAgent(): Observable<ProvisionContractAgentDto> {

        if (!this.adjustsAgent()) {
            return of(null);
        }

        return this.state.ensureAgentUpdated(this.buildAgentPayload());
    }

    /**
     * The update payload of the Provisionszeile — the captured values plus the id.
     *
     * Only fields the form actually carries a control for appear here, which is what makes the
     * partial update safe: everything excluded has no key and is therefore left untouched by the
     * generic merge.
     */
    protected buildContractPayload(): DtoDetail {

        const payload = this.state.copyValue(this.state.contractDraft);

        payload.id = this.provisionContractId;

        return payload;
    }

    /** The update payload of the Provisionssatz — the captured values plus the id. */
    protected buildAgentPayload(): DtoDetail {

        const payload = this.state.copyValue(this.state.agentDraft);

        payload.id = this.provisionContractAgentId;

        return payload;
    }

    protected handleSaveSuccess(): void {

        this.state.endSaveSequence();
        this.state.saveError = null;
        this.state.saveHint = null;

        this.completed = true;

        this.messageService.showSuccessMessage(
            'Änderungen übernommen',
            'Die manuelle Anpassung wurde gespeichert.');

        this.informObjectChanged();
        this.onAdjusted.emit();
    }

    protected handleSaveError(error: any): void {

        this.state.endSaveSequence();
        this.state.resolveErrorMessage(error);

        this.demoteOnForbidden(error);

        if (CpFeatureRight.isForbidden(error)) {
            this.state.saveError = CpFeatureRight.deniedMessage(CpFeatureRight.AGENT_CHANGE);
            this.state.saveHint = null;
        }

        this.messageService.showErrorMessage('Anpassung fehlgeschlagen', this.state.saveError);
    }

    /** ONLY a 403 demotes — see the identical rule in the create wizard. */
    protected demoteOnForbidden(error: any): void {

        if (CpFeatureRight.isForbidden(error)) {
            this.canAgentChange = false;
            this.onForbidden.emit();
        }
    }

    /** Tell the module which rows changed — hosting lists and detail panes reload on it. */
    protected informObjectChanged(): void {

        // What was WRITTEN, not what was in scope: after a partial failure the scope still names both
        // rows while only one of them changed, and announcing a change that did not happen sends every
        // listener off to refresh a row that stands exactly as it did.
        if (this.state.contractUpdated) {
            this.mvsObjectService.objectChangeEvent.next(
                ObjectChangeInformation.create(
                    CpProvisionContractWizardStateService.ALIAS_PROVISION_CONTRACT,
                    ObjectChangeInformationActionEnum.updated,
                    null,
                    this.state.createdContract));
        }

        if (this.state.agentUpdated) {
            this.mvsObjectService.objectChangeEvent.next(
                ObjectChangeInformation.create(
                    CpProvisionContractWizardStateService.ALIAS_PROVISION_CONTRACT_AGENT,
                    ObjectChangeInformationActionEnum.updated,
                    null,
                    this.state.updatedAgent));
        }
    }

    // ---------------------------------------------------------------------------------------- //
    // Dialog frame
    // ---------------------------------------------------------------------------------------- //

    /**
     * Abandon the run; refused while a write is in flight — see the create wizard.
     *
     * <p>NO ORPHAN IS POSSIBLE HERE, and the difference is worth stating rather than copying the
     * create wizard's wording: this wizard only ever UPDATES rows that already exist, so an abandoned
     * run leaves nothing behind that was not there before. What it can leave is a PARTIAL CHANGE —
     * the Provisionszeile written, the Provisionssatz not — and that is just as invisible to the user
     * if nobody says it. So it is said, and the changed row is announced so the surrounding surfaces
     * show its new state instead of the old one.
     */
    handleCancel(): void {

        if (this.state.saveBusy) {
            return;
        }

        if (this.state.hasPartialResult()) {

            this.messageService.showWarningMessage(
                'Teilweise übernommen',
                'Ein Teil der Änderungen wurde bereits gespeichert und wird durch den Abbruch nicht '
                + 'zurückgenommen. Prüfen Sie den Stand der Zeile und des Satzes, bevor Sie erneut '
                + 'anpassen.');

            this.informObjectChanged();
        }

        this.onCancel.emit();
        this.handleClose();
    }

    handleShow(): void {
        this.refreshComponent();
    }

    handleClose(): void {

        if (this.state.saveBusy) {
            return;
        }

        this.visible = false;
        this.visibleChange.emit(false);
    }

    handleVisibleChange(value: boolean): void {

        if (this.state.saveBusy && !value) {
            return;
        }

        this.visible = value;
        this.visibleChange.emit(value);
    }

}
